import React, { useRef, useState } from 'react';
import type { WeightMeasurement } from '../../api/dietApi';

interface WeightChartProps {
  measurements: WeightMeasurement[];
}

const fmt = (n: number) => n.toFixed(1).replace('.', ',');

/**
 * SVG-графік ваги — точна копія прототипу prototyp-panel-pacjenta_8.html
 * Параметри: W=311, H=88, PAD=14, TOP=10, BOT=64
 * Лінія: #361777, 2px. Заливка: градієнт rgba(54,23,119,.15)→transparent.
 * Точки: r=4 (остання r=5), stroke=#E6D6FA 2.5px.
 * Вісь X: тільки перша та остання дата (DD.MM).
 * Тултип на тап/hover: "DD.MM · 112,1 kg".
 */
export const WeightChart: React.FC<WeightChartProps> = ({ measurements }) => {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
  const boxRef = useRef<SVGSVGElement>(null);

  if (measurements.length < 2) return null;

  const W = 311, H = 88, PAD = 14, TOP = 10, BOT = 64;
  const vals = measurements.map(m => m.weight);
  const mn = Math.min(...vals);
  const mx = Math.max(...vals);
  const sp = mx - mn || 1;

  const pts = measurements.map((m, i) => ({
    x: PAD + (W - PAD * 2) * (i / (measurements.length - 1)),
    y: TOP + (BOT - TOP) * ((mx - m.weight) / sp) * 0.9,
    date: m.date,
    weight: m.weight,
  }));

  const linePath = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${pts[pts.length - 1].x.toFixed(1)} ${BOT + 4} L${pts[0].x.toFixed(1)} ${BOT + 4} Z`;

  const handlePointInteraction = (p: typeof pts[0], e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setTooltip({ x: p.x, y: p.y, text: `${p.date} · ${fmt(p.weight)} kg` });
  };

  return (
    <div style={{ position: 'relative', marginTop: 10 }}>
      <svg
        ref={boxRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ fontFamily: 'Poppins', overflow: 'visible' }}
        onMouseLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id="wg-chart" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#361777" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#361777" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Область заливки */}
        <path d={areaPath} fill="url(#wg-chart)" />

        {/* Лінія */}
        <path
          d={linePath}
          fill="none"
          stroke="#361777"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Точки */}
        {pts.map((p, i) => (
          <circle
            key={i}
            cx={p.x.toFixed(1)}
            cy={p.y.toFixed(1)}
            r={i === pts.length - 1 ? 5 : 4}
            fill="#361777"
            stroke="#E6D6FA"
            strokeWidth="2.5"
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => handlePointInteraction(p, e)}
            onTouchStart={(e) => handlePointInteraction(p, e)}
            onTouchEnd={() => setTimeout(() => setTooltip(null), 2000)}
          />
        ))}

        {/* Підписи осі X — тільки перша та остання дата */}
        <text x={pts[0].x} y={H - 3} fontSize="10" fontWeight="500" fill="#AB9CC9" textAnchor="start">
          {pts[0].date}
        </text>
        <text x={pts[pts.length - 1].x} y={H - 3} fontSize="10" fontWeight="500" fill="#AB9CC9" textAnchor="end">
          {pts[pts.length - 1].date}
        </text>
      </svg>

      {/* Тултип */}
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            background: '#361777',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 9px',
            borderRadius: 9,
            pointerEvents: 'none',
            transform: 'translate(-50%, -135%)',
            whiteSpace: 'nowrap',
            left: `${(tooltip.x / W) * 100}%`,
            top: `${(tooltip.y / H) * 100}%`,
            zIndex: 10,
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};
